import React, { memo, useCallback, useState } from 'react';
import DropdownMenu, { type CustomTriggerProps } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import { Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import messages from './messages.tsx';

type AddTabProps = {
	menuContent: React.ComponentType<{ closeDropdown: () => void }>;
};

const AddTab = ({ menuContent: MenuContent }: AddTabProps) => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState<boolean>(false);

	const onOpenChange = useCallback(() => {
		const event = isDropdownMenuOpen ? 'closeAddViewMenu' : 'addViewMenu';
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', event);
		setIsDropdownMenuOpen((isDropdownMenuCurrent) => !isDropdownMenuCurrent);
	}, [createAnalyticsEvent, isDropdownMenuOpen]);

	const closeDropdown = useCallback(() => {
		setIsDropdownMenuOpen(false);
	}, []);

	const trigger = ({
		triggerRef,
		isSelected: isMenuOpen,
		onClick,
		...triggerProps
	}: CustomTriggerProps) => (
		<Inline alignBlock="end" xcss={inlineActionStyles}>
			<Tooltip content={formatMessage(messages.triggerLabel)} hideTooltipOnMouseDown>
				<ThemedButton
					spacing="none"
					appearance="subtle"
					name={formatMessage(messages.triggerLabel)}
					ref={triggerRef}
					onClick={(event) => {
						onClick && onClick(event);
					}}
					isSelected={isDropdownMenuOpen}
					{...triggerProps}
					testId="navigation-kit-add-tab-old.ui.trigger"
				>
					<EditorAddIcon
						LEGACY_size="medium"
						spacing="spacious"
						label={formatMessage(messages.triggerLabel)}
					/>
				</ThemedButton>
			</Tooltip>
		</Inline>
	);

	return (
		<DropdownMenu
			trigger={trigger}
			placement="bottom-start"
			testId="navigation-kit-add-tab-old.ui.dropdown-menu"
			isOpen={isDropdownMenuOpen}
			onOpenChange={onOpenChange}
		>
			<MenuContent closeDropdown={closeDropdown} />
		</DropdownMenu>
	);
};

export default memo(AddTab);

const inlineActionStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '0',
	position: 'relative',
	alignSelf: 'start',
	paddingBlockStart: 'space.075',
});
